
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SkillsSpiderChart from './SkillsSpiderChart';
import { useProfileData } from '@/hooks/useProfileData';
import PersonalInfoSection from './profile-sections/PersonalInfoSection';
import ExperienceSection from './profile-sections/ExperienceSection';
import EducationSection from './profile-sections/EducationSection';
import ProjectsSection from './profile-sections/ProjectsSection';

interface ProfileSummaryCardProps {
  userId: string;
}

const ProfileSummaryCard: React.FC<ProfileSummaryCardProps> = ({ userId }) => {
  const navigate = useNavigate();
  const { profile, education, experience, skills, projects: initialProjects, loading } = useProfileData(userId);
  const [projects, setProjects] = useState(initialProjects);

  // Update projects state when initialProjects changes
  React.useEffect(() => {
    if (initialProjects) {
      setProjects(initialProjects);
    }
  }, [initialProjects]);

  const handleEditProfile = () => {
    navigate('/job-seeker-profile');
  };

  if (loading) {
    return (
      <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
        <CardHeader>
          <CardTitle className="text-xl text-gray-800">Profile Summary</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center items-center h-96">
          <div className="text-gray-500">Loading profile data...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6">
      <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
        <CardHeader>
          <CardTitle className="text-xl text-gray-800">Profile Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <PersonalInfoSection 
            headline={profile?.headline} 
            location={profile?.location} 
          />

          <ExperienceSection experience={experience} />

          <EducationSection education={education} />

          <ProjectsSection 
            userId={userId}
            projects={projects}
            setProjects={setProjects}
          />

          <Button 
            className="w-full mt-4 bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90"
            onClick={handleEditProfile}
          >
            Edit Full Profile
          </Button>
        </CardContent>
      </Card>
      
      <SkillsSpiderChart skills={skills} />
    </div>
  );
};

export default ProfileSummaryCard;
